import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Cookie, X, ChevronDown, ChevronUp } from 'lucide-react'
import { motion, AnimatePresence } from 'motion/react'
import { useLanguage } from '@/context/LanguageContext'

const CONSENT_KEY = 'cookie-consent-v2'
const CONSENT_EXPIRY_DAYS = 180

export type CookieCategory = 'necessary' | 'analytics'

export interface CookieConsent {
  necessary: boolean
  analytics: boolean
  expires: number
}

const DEFAULT_CONSENT: CookieConsent = {
  necessary: true,
  analytics: false,
  expires: 0,
}

export function getConsent(): CookieConsent | null {
  try {
    const raw = localStorage.getItem(CONSENT_KEY)
    if (!raw) return null
    const data = JSON.parse(raw)
    if (data?.expires && Date.now() > data.expires) {
      localStorage.removeItem(CONSENT_KEY)
      return null
    }
    return {
      necessary: true,
      analytics: data?.analytics === true,
      expires: data?.expires ?? 0,
    }
  } catch {
    return null
  }
}

export function hasAnalyticsConsent(): boolean {
  const c = getConsent()
  return c?.analytics === true
}

function saveConsent(choices: Pick<CookieConsent, 'analytics'>) {
  const consent: CookieConsent = {
    necessary: true,
    analytics: choices.analytics,
    expires: Date.now() + CONSENT_EXPIRY_DAYS * 24 * 60 * 60 * 1000,
  }
  localStorage.setItem(CONSENT_KEY, JSON.stringify(consent))
  window.dispatchEvent(new Event('cookie-consent-changed'))
}

export function revokeConsent() {
  localStorage.removeItem(CONSENT_KEY)
  window.dispatchEvent(new Event('cookie-consent-changed'))
  window.location.reload()
}

const COOKIE_CATEGORIES = [
  {
    id: 'necessary' as CookieCategory,
    title: { it: 'Cookie necessari', en: 'Necessary cookies' },
    description: {
      it: 'Essenziali per il funzionamento del sito: autenticazione, sessione utente e sicurezza. Non possono essere disattivati.',
      en: 'Essential for the site to function: authentication, user session and security. Cannot be disabled.',
    },
    required: true,
  },
  {
    id: 'analytics' as CookieCategory,
    title: { it: 'Cookie analitici', en: 'Analytics cookies' },
    description: {
      it: 'Ci aiutano a capire come usi il sito raccogliendo dati aggregati (pagine visitate, tempo trascorso). Utilizzati anonimamente per migliorare l\'esperienza.',
      en: 'Help us understand how you use the site by collecting aggregate data (pages visited, time spent). Used anonymously to improve the experience.',
    },
    required: false,
  },
]

export default function CookieBanner() {
  const { language } = useLanguage()
  const [visible, setVisible] = useState(false)
  const [showDetails, setShowDetails] = useState(false)
  const [analytics, setAnalytics] = useState(false)

  useEffect(() => {
    if (!getConsent()) {
      const timer = setTimeout(() => setVisible(true), 800)
      return () => clearTimeout(timer)
    }
  }, [])

  const handleAcceptAll = () => {
    saveConsent({ analytics: true })
    setVisible(false)
  }

  const handleRejectAll = () => {
    saveConsent({ analytics: false })
    setVisible(false)
  }

  const handleSavePreferences = () => {
    saveConsent({ analytics })
    setVisible(false)
  }

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          className="fixed bottom-0 left-0 right-0 z-[80] p-4 sm:p-6 flex justify-center"
          initial={{ y: 120, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 120, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 260, damping: 28 }}
        >
          <div className="mx-auto max-w-md rounded-2xl bg-white border border-neutral-200 shadow-lg p-5 sm:p-5 relative">
            <button
              type="button"
              aria-label={language === 'en' ? 'Close' : 'Chiudi'}
              onClick={handleRejectAll}
              className="absolute top-3 right-3 w-7 h-7 flex items-center justify-center rounded-full text-neutral-400 hover:text-neutral-900 hover:bg-neutral-100 transition-colors"
            >
              <X className="w-3.5 h-3.5" />
            </button>
            <div className="flex items-start gap-3 text-left">
              <span className="flex items-center justify-center w-9 h-9 rounded-lg bg-neutral-900 text-white flex-shrink-0 mt-0.5">
                <Cookie className="w-4 h-4" />
              </span>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-bold text-neutral-900 mb-1">
                  {language === 'en' ? 'Before we begin, a cookie?' : 'Prima di iniziare, un cookie?'}
                </h3>
                <p className="text-xs text-neutral-600 leading-relaxed">
                  {language === 'en'
                    ? 'Not the chocolate chip kind, sadly. 🍪 We use technical cookies to run the site and analytics cookies to understand how to improve it. You can choose which to accept and read the '
                    : 'Non quelli con le gocce di cioccolato, purtroppo. 🍪 Usiamo cookie tecnici per far girare il sito e analitici per capire come migliorarlo. Puoi scegliere quali accettare e leggere la '}
                  {' '}
                  <Link to="/cookie-policy" className="text-indigo-600 font-medium hover:underline">
                    Cookie Policy
                  </Link>
                </p>

                <AnimatePresence>
                  {showDetails && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="mt-3 space-y-2.5">
                        {COOKIE_CATEGORIES.map((cat) => (
                          <div
                            key={cat.id}
                            className="flex items-start gap-2.5 rounded-lg bg-neutral-50 p-2.5"
                          >
                            <button
                              type="button"
                              disabled={cat.required}
                              onClick={() => {
                                if (cat.id === 'analytics') setAnalytics((v) => !v)
                              }}
                              className={`mt-0.5 flex-shrink-0 relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                                (cat.id === 'analytics' ? analytics : true)
                                  ? 'bg-neutral-900'
                                  : 'bg-neutral-300'
                              } ${cat.required ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                              aria-label={`Toggle ${cat.title[language]}`}
                            >
                              <span
                                className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
                                  (cat.id === 'analytics' ? analytics : true)
                                    ? 'translate-x-5'
                                    : 'translate-x-1'
                                }`}
                              />
                            </button>
                            <div className="min-w-0">
                              <p className="text-xs font-semibold text-neutral-900">
                                {cat.title[language]}
                                {cat.required && (
                                  <span className="ml-1 text-[10px] font-normal text-neutral-400">
                                    {language === 'en' ? '(always active)' : '(sempre attivi)'}
                                  </span>
                                )}
                              </p>
                              <p className="text-[11px] text-neutral-500 leading-snug mt-0.5">
                                {cat.description[language]}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                <div className="flex gap-2 mt-3">
                  <button
                    type="button"
                    onClick={handleAcceptAll}
                    className="flex-1 inline-flex items-center justify-center rounded-full bg-neutral-900 px-4 py-2 text-xs font-medium text-white transition hover:bg-neutral-800"
                  >
                    {language === 'en' ? 'Accept all' : 'Accetta tutti'}
                  </button>
                  <button
                    type="button"
                    onClick={handleRejectAll}
                    className="flex-1 inline-flex items-center justify-center rounded-full border border-neutral-300 px-4 py-2 text-xs font-medium text-neutral-700 transition hover:bg-neutral-50"
                  >
                    {language === 'en' ? 'Reject' : 'Rifiuta'}
                  </button>
                </div>

                <button
                  type="button"
                  onClick={() => setShowDetails((v) => !v)}
                  className="mt-2.5 w-full inline-flex items-center justify-center gap-1 text-[11px] font-medium text-neutral-500 hover:text-neutral-900 transition-colors"
                >
                  {showDetails ? (
                    <>
                      <ChevronUp className="w-3 h-3" />
                      {language === 'en' ? 'Close details' : 'Chiudi dettagli'}
                    </>
                  ) : (
                    <>
                      <ChevronDown className="w-3 h-3" />
                      {language === 'en' ? 'Customize preferences' : 'Personalizza preferenze'}
                    </>
                  )}
                </button>

                <AnimatePresence>
                  {showDetails && (
                    <motion.button
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      type="button"
                      onClick={handleSavePreferences}
                      className="mt-2 w-full inline-flex items-center justify-center rounded-full bg-indigo-600 px-4 py-2 text-xs font-medium text-white transition hover:bg-indigo-700"
                    >
                      {language === 'en' ? 'Save preferences' : 'Salva preferenze'}
                    </motion.button>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
